<template>
  <agenda-calendar-skeleton v-if="loading"/>

  <div v-else class="agenda-calendar" :class="'agenda-calendar--' + viewMode">
    <!-- Ay görünümü -->
    <template v-if="viewMode === 'month'">
      <div class="agenda-calendar__main">
        <div class="agenda-calendar__toolbar">
          <button class="agenda-calendar__nav" type="button" @click="prevMonth">
            <i class="uil-angle-left"></i>
          </button>
          <div class="agenda-calendar__toolbar-center">
            <h3 class="agenda-calendar__period">{{ monthLabel }}</h3>
            <button
              v-if="showGoToTodayInMonth"
              class="agenda-calendar__today"
              type="button"
              :title="goToTodayLabel"
              @click="goToToday"
            >
              <i class="uil-focus-target"></i>
              {{ goToTodayLabel }}
            </button>
          </div>
          <button class="agenda-calendar__nav" type="button" @click="nextMonth">
            <i class="uil-angle-right"></i>
          </button>
        </div>

        <div class="agenda-calendar__weekdays">
          <span v-for="(day, index) in weekdays" :key="'wd_' + index" class="agenda-calendar__weekday">
            {{ day }}
          </span>
        </div>

        <div class="agenda-calendar__grid">
          <button
            v-for="(cell, index) in calendarCells"
            :key="'cell_' + index"
            type="button"
            class="agenda-calendar__cell"
            :class="{
              'agenda-calendar__cell--other': !cell.current,
              'agenda-calendar__cell--today': cell.isToday,
              'agenda-calendar__cell--selected': cell.isSelected,
              'agenda-calendar__cell--has-events': cell.eventCount > 0,
              'agenda-calendar__cell--weekend': cell.isWeekend,
            }"
            @click="selectDay(cell)"
          >
            <span class="agenda-calendar__day">{{ cell.day }}</span>
            <span v-if="cell.eventCount" class="agenda-calendar__count">{{ cell.eventCount }}</span>
            <span v-if="cell.eventCount" class="agenda-calendar__cell-dots">
              <span v-if="cell.hasAssignment" class="agenda-calendar__cell-dot agenda-calendar__cell-dot--assignment"></span>
              <span v-if="cell.hasTraining" class="agenda-calendar__cell-dot agenda-calendar__cell-dot--training"></span>
              <span v-if="cell.hasNotice" class="agenda-calendar__cell-dot agenda-calendar__cell-dot--notice"></span>
              <span v-if="cell.hasUpcoming" class="agenda-calendar__cell-dot agenda-calendar__cell-dot--upcoming"></span>
              <span v-if="cell.hasDeadlineSoon" class="agenda-calendar__cell-dot agenda-calendar__cell-dot--due-warn"></span>
            </span>
          </button>
        </div>

        <div class="agenda-calendar__legend">
          <span class="agenda-calendar__legend-item">
            <span class="agenda-calendar__legend-dot agenda-calendar__legend-dot--assignment"></span>
            {{ $t('general.assigned_educations') || 'Atanan Eğitim' }}
          </span>
          <span class="agenda-calendar__legend-item">
            <span class="agenda-calendar__legend-dot agenda-calendar__legend-dot--training"></span>
            {{ $t('education.catalog_education') }}
          </span>
          <span class="agenda-calendar__legend-item">
            <span class="agenda-calendar__legend-dot agenda-calendar__legend-dot--notice"></span>
            {{ $t('general.announcement') }}
          </span>
          <span class="agenda-calendar__legend-item">
            <span class="agenda-calendar__legend-dot agenda-calendar__legend-dot--upcoming"></span>
            {{ $t('general.upcoming') || 'Yaklaşan' }}
          </span>
          <span class="agenda-calendar__legend-item">
            <span class="agenda-calendar__legend-dot agenda-calendar__legend-dot--due-warn"></span>
            {{ $t('general.deadline_soon') || 'Süresi dolmak üzere' }}
          </span>
        </div>

        <div v-if="sideNotifications.length" class="agenda-calendar__notifications">
          <h5 class="agenda-calendar__notifications-title">
            <i class="uil-bell"></i>
            {{ $t('general.notifications') || 'Bildirimler' }}
            <span class="agenda-calendar__notifications-count">{{ sideNotifications.length }}</span>
          </h5>
          <button
            v-for="item in sideNotifications"
            :key="'notif_' + item.id + '_' + item.type + '_' + item._notifType"
            type="button"
            class="agenda-calendar__notif-item"
            :class="'agenda-calendar__notif-item--' + item._notifType"
            @click="onItemClick(item)"
          >
            <span class="agenda-calendar__notif-badge" :class="'agenda-calendar__notif-badge--' + item._notifType">
              {{ item._notifType === 'due-warn'
                ? ($t('general.deadline_soon') || 'Süresi dolmak üzere')
                : ($t('general.upcoming') || 'Yaklaşan')
              }}
            </span>
            <span class="agenda-calendar__notif-name">{{ item.title }}</span>
            <span class="agenda-calendar__notif-date">{{ item._notifDate }}</span>
          </button>
        </div>
      </div>

      <aside class="agenda-calendar__side">
        <div class="agenda-calendar__side-header">
          <div>
            <h4 class="agenda-calendar__side-title">{{ selectedDateLabel }}</h4>
          </div>
          <span v-if="selectedDayItems.length" class="agenda-calendar__side-badge">{{ selectedDayItems.length }}</span>
        </div>

        <div v-if="selectedDayItems.length" class="agenda-calendar__events">
          <article
            v-for="item in selectedDayItems"
            :key="item.id + '_' + item.type"
            class="agenda-calendar__event"
            :class="{
              'agenda-calendar__event--due-warn': isDeadlineSoon(item),
              'agenda-calendar__event--upcoming': isUpcoming(item),
              'agenda-calendar__event--expired': item.isExpired,
              ['agenda-calendar__event--' + item.type]: !item.isExpired,
            }"
            @click="onItemClick(item)"
          >
            <div class="agenda-calendar__event-top">
              <span
                class="agenda-calendar__event-type"
                :class="'agenda-calendar__event-type--' + item.type"
              >
                {{ typeLabel(item) }}
              </span>
              <span v-if="item.isExpired" class="agenda-calendar__event-expired">
                {{ $t('general.expired') || 'Süresi geçmiş' }}
              </span>
              <span v-else-if="isDeadlineSoon(item)" class="agenda-calendar__event-due-warn">
                {{ $t('general.deadline_soon') || 'Süresi dolmak üzere' }}
              </span>
              <span v-else-if="isUpcoming(item)" class="agenda-calendar__event-upcoming">
                {{ $t('general.upcoming') || 'Yaklaşan' }}
              </span>
            </div>
            <h5 class="agenda-calendar__event-title">{{ item.title }}</h5>
            <p class="agenda-calendar__event-date">
              <i class="uil-calendar-alt"></i>
              {{ formatItemDate(item) }}
            </p>
          </article>
        </div>

        <div v-else class="agenda-calendar__empty">
          <i class="uil-calendar-slash"></i>
          <p>{{ $t('general.no_records_today') }}</p>
        </div>

      </aside>
    </template>

    <!-- Hafta görünümü -->
    <template v-else-if="viewMode === 'week'">
      <div class="agenda-calendar__week-toolbar">
        <button class="agenda-calendar__nav" type="button" @click="prevWeek">
          <i class="uil-angle-left"></i>
        </button>
        <div class="agenda-calendar__toolbar-center">
          <h3 class="agenda-calendar__period">{{ weekLabel }}</h3>
          <button
            v-if="showGoToTodayInWeek"
            class="agenda-calendar__today"
            type="button"
            :title="goToTodayLabel"
            @click="goToToday"
          >
            <i class="uil-focus-target"></i>
            {{ goToTodayLabel }}
          </button>
        </div>
        <button class="agenda-calendar__nav" type="button" @click="nextWeek">
          <i class="uil-angle-right"></i>
        </button>
      </div>

      <div class="agenda-calendar__week-grid">
        <section
          v-for="day in weekDays"
          :key="day.key"
          class="agenda-calendar__week-day"
          :class="{
            'agenda-calendar__week-day--today': day.isToday,
            'agenda-calendar__week-day--selected': day.isSelected,
          }"
        >
          <div class="agenda-calendar__week-day-head">
            <span class="agenda-calendar__week-day-name">{{ day.weekday }}</span>
            <span class="agenda-calendar__week-day-num">{{ day.formattedDate }}</span>
            <span v-if="day.eventCount" class="agenda-calendar__week-day-count">{{ day.eventCount }}</span>
          </div>
          <div class="agenda-calendar__week-events">
            <button
              v-for="item in day.items"
              :key="item.id + '_' + item.type"
              type="button"
              class="agenda-calendar__week-event"
              :class="{
                'agenda-calendar__week-event--due-warn': isDeadlineSoon(item),
                'agenda-calendar__week-event--upcoming': isUpcoming(item),
                'agenda-calendar__week-event--expired': item.isExpired,
              }"
              @click="onItemClick(item)"
            >
              <span
                class="agenda-calendar__week-event-type"
                :class="[
                  'agenda-calendar__event-type--' + item.type,
                  { 'agenda-calendar__week-event-type--expired': item.isExpired },
                ]"
              ></span>
              <span class="agenda-calendar__week-event-title">{{ item.title }}</span>
            </button>
            <p v-if="!day.items.length" class="agenda-calendar__week-empty">—</p>
          </div>
        </section>
      </div>
    </template>

    <!-- Yıl görünümü -->
    <template v-else>
      <div class="agenda-calendar__year-toolbar">
        <button class="agenda-calendar__nav" type="button" @click="prevYear">
          <i class="uil-angle-left"></i>
        </button>
        <h3 class="agenda-calendar__period">{{ currentYear }}</h3>
        <button class="agenda-calendar__nav" type="button" @click="nextYear">
          <i class="uil-angle-right"></i>
        </button>
      </div>

      <div class="agenda-calendar__year-grid">
        <button
          v-for="month in yearMonths"
          :key="month.index"
          type="button"
          class="agenda-calendar__year-month"
          :class="{ 'agenda-calendar__year-month--current': month.isCurrent }"
          @click="openMonth(month.index)"
        >
          <span class="agenda-calendar__year-month-name">{{ month.label }}</span>
          <span class="agenda-calendar__year-month-count">{{ month.eventCount }}</span>
          <span class="agenda-calendar__year-month-sub">{{ $t('general.record') }}</span>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import moment from "moment";
import AgendaCalendarSkeleton from "./AgendaCalendarSkeleton.vue";

const AZ_WEEKDAYS = ["B.", "B.e.", "Ç.a.", "Ç.", "C.a.", "C.", "Ş."];

export default {
  name: "AgendaCalendar",
  components: { AgendaCalendarSkeleton },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    viewMode: {
      type: String,
      default: "month",
      validator: (value) => ["week", "month", "year"].includes(value),
    },
    useCustomLanguage: {
      type: Boolean,
      default: false,
    },
    summaryData: {
      type: Array,
      default: () => [],
    },
    notificationItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const now = new Date();
    return {
      currentYear: now.getFullYear(),
      currentMonth: now.getMonth(),
      currentWeekStart: null,
      selectedDate: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    };
  },
  computed: {
    locale() {
      return this.$i18n?.locale || "tr";
    },
    weekdays() {
      if (this.useCustomLanguage) return AZ_WEEKDAYS;
      const wd = moment.localeData(this.locale).weekdaysMin();
      return [...wd.slice(1), wd[0]];
    },
    monthLabel() {
      return moment({ year: this.currentYear, month: this.currentMonth }).locale(this.locale).format("MMMM YYYY");
    },
    weekLabel() {
      const start = moment(this.weekStartDate);
      const end = moment(this.weekStartDate).add(6, "days");
      if (start.month() === end.month()) {
        return `${start.format("D")} – ${end.format("D MMMM YYYY")}`;
      }
      return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
    },
    isCurrentMonth() {
      const now = new Date();
      return this.currentYear === now.getFullYear() && this.currentMonth === now.getMonth();
    },
    isTodaySelected() {
      if (!this.selectedDate) return false;
      return this.dateKey(this.selectedDate) === this.dateKey(new Date());
    },
    goToTodayLabel() {
      return this.$t("general.go_to_today") || "Bugüne git";
    },
    showGoToTodayInMonth() {
      return !this.isCurrentMonth || !this.isTodaySelected;
    },
    showGoToTodayInWeek() {
      return !this.isCurrentWeek || !this.isTodaySelected;
    },
    isCurrentWeek() {
      const now = new Date();
      const start = this.weekStartDate;
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return now >= start && now <= end;
    },
    weekStartDate() {
      if (this.currentWeekStart) return this.currentWeekStart;
      return this.getWeekStart(this.selectedDate);
    },
    selectedDateLabel() {
      if (!this.selectedDate) return "";
      const m = moment(this.selectedDate).locale(this.locale);
      if (this.dateKey(this.selectedDate) === this.dateKey(new Date())) {
        return `${this.$t("general.today")}, ${m.format("D MMMM")}`;
      }
      return m.format("D MMMM YYYY");
    },
    normalizedNotificationItems() {
      return (this.notificationItems || []).map(item => ({
        ...item,
        startDate: item.startDate instanceof Date ? item.startDate : new Date(item.startDate),
        endDate: item.endDate instanceof Date ? item.endDate : new Date(item.endDate),
      }));
    },
    normalizedItems() {
      return (this.items || []).map(item => ({
        ...item,
        startDate: item.startDate instanceof Date ? item.startDate : new Date(item.startDate),
        endDate: item.endDate instanceof Date ? item.endDate : new Date(item.endDate),
      }));
    },
    eventDateMap() {
      const map = {};
      this.normalizedItems.forEach(item => {
        this.eachDayInRange(item.startDate, item.endDate, date => {
          const key = this.dateKey(date);
          if (!map[key]) map[key] = [];
          map[key].push(item);
        });
      });
      return map;
    },
    calendarCells() {
      const cells = [];
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
      const todayKey = this.dateKey(new Date());
      const selectedKey = this.selectedDate ? this.dateKey(this.selectedDate) : null;
      let startDow = (firstDay.getDay() + 6) % 7;

      const prevLast = new Date(this.currentYear, this.currentMonth, 0);
      for (let i = startDow - 1; i >= 0; i--) {
        const day = prevLast.getDate() - i;
        const date = new Date(this.currentYear, this.currentMonth - 1, day);
        cells.push(this.makeCell(date, day, false, todayKey, selectedKey));
      }

      for (let day = 1; day <= lastDay.getDate(); day++) {
        const date = new Date(this.currentYear, this.currentMonth, day);
        cells.push(this.makeCell(date, day, true, todayKey, selectedKey));
      }

      const remaining = 7 - (cells.length % 7);
      if (remaining < 7) {
        for (let day = 1; day <= remaining; day++) {
          const date = new Date(this.currentYear, this.currentMonth + 1, day);
          cells.push(this.makeCell(date, day, false, todayKey, selectedKey));
        }
      }

      return cells;
    },
    selectedDayItems() {
      return this.itemsForDate(this.selectedDate);
    },
    upcomingItems() {
      const today = moment().startOf("day");
      const inFourteenDays = today.clone().add(14, "days");

      return this.normalizedNotificationItems
        .filter(item => {
          if (item.isExpired) return false;
          const start = moment(item.startDate).startOf("day");
          return start.isAfter(today, "day") && start.isSameOrBefore(inFourteenDays, "day");
        })
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
        .slice(0, 5);
    },
    sideNotifications() {
      const deadlines = this.deadlineSoonItems.map(item => ({
        ...item,
        _notifType: "due-warn",
        _notifDate: this.formatShortDate(item.deadlineDate || item.endDate),
        _sortDate: new Date(item.deadlineDate || item.endDate),
      }));
      const upcomings = this.upcomingItems.map(item => ({
        ...item,
        _notifType: "upcoming",
        _notifDate: this.formatShortDate(item.startDate),
        _sortDate: new Date(item.startDate),
      }));
      return [...deadlines, ...upcomings].sort((a, b) => a._sortDate - b._sortDate).slice(0, 6);
    },
    deadlineSoonItems() {
      const now = moment();
      const today = now.clone().startOf("day");
      const inSevenDays = today.clone().add(7, "days");

      return this.normalizedNotificationItems
        .filter(item => {
          if (item.type !== "assignment" || item.isExpired) return false;

          const deadline = item.deadlineDate || item.endDate;
          if (!deadline) return false;

          const end = moment(deadline);
          if (end.isSameOrBefore(now)) return false;

          const endDay = end.clone().startOf("day");
          return endDay.isSameOrAfter(today, "day") && endDay.isSameOrBefore(inSevenDays, "day");
        })
        .sort((a, b) => new Date(a.deadlineDate || a.endDate) - new Date(b.deadlineDate || b.endDate))
        .slice(0, 5);
    },
    weekDays() {
      const days = [];
      const selectedKey = this.selectedDate ? this.dateKey(this.selectedDate) : null;
      const todayKey = this.dateKey(new Date());

      for (let i = 0; i < 7; i++) {
        const date = new Date(this.weekStartDate);
        date.setDate(date.getDate() + i);
        const key = this.dateKey(date);
        const items = this.itemsForDate(date);
        days.push({
          key,
          date,
          day: date.getDate(),
          formattedDate: moment(date).locale(this.locale).format("D MMM"),
          weekday: this.useCustomLanguage
            ? AZ_WEEKDAYS[i]
            : moment(date).locale(this.locale).format("ddd"),
          isToday: key === todayKey,
          isSelected: key === selectedKey,
          eventCount: items.length,
          items,
        });
      }

      return days;
    },
    yearMonths() {
      const now = new Date();
      const summaryMap = {};
      (this.summaryData || []).forEach(entry => {
        summaryMap[entry.month] = entry.count || 0;
      });

      return Array.from({ length: 12 }, (_, index) => ({
        index,
        label: moment({ month: index }).locale(this.locale).format("MMMM"),
        eventCount: summaryMap[index + 1] || 0,
        isCurrent: now.getFullYear() === this.currentYear && now.getMonth() === index,
      }));
    },
  },
  watch: {
    viewMode(newMode) {
      if (newMode === "week") {
        this.currentWeekStart = this.getWeekStart(this.selectedDate || new Date());
      }
      this.$nextTick(() => this.emitDateRange());
    },
  },
  mounted() {
    this.emitDateRange();
  },
  methods: {
    dateKey(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    },
    eachDayInRange(start, end, callback) {
      const cursor = new Date(start);
      cursor.setHours(0, 0, 0, 0);
      const last = new Date(end);
      last.setHours(0, 0, 0, 0);
      while (cursor <= last) {
        callback(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    },
    makeCell(date, day, current, todayKey, selectedKey) {
      const key = this.dateKey(date);
      const dow = (date.getDay() + 6) % 7;
      const events = this.eventDateMap[key] || [];
      return {
        day,
        date,
        current,
        isToday: key === todayKey,
        isSelected: key === selectedKey,
        eventCount: events.length,
        isWeekend: dow >= 5,
        hasAssignment: events.some(e => e.type === "assignment"),
        hasTraining: events.some(e => e.type === "training"),
        hasNotice: events.some(e => e.type === "notice"),
        hasUpcoming: events.some(e => this.isUpcoming(e)),
        hasDeadlineSoon: events.some(e => this.isDeadlineSoon(e)),
      };
    },
    itemsForDate(date) {
      if (!date) return [];
      const key = this.dateKey(date);
      return this.eventDateMap[key] || [];
    },
    getWeekStart(date) {
      const d = new Date(date);
      const day = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - day);
      d.setHours(0, 0, 0, 0);
      return d;
    },
    selectDay(cell) {
      this.selectedDate = cell.date;
      if (!cell.current) {
        this.currentYear = cell.date.getFullYear();
        this.currentMonth = cell.date.getMonth();
      }
    },
    selectDate(date) {
      this.selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    },
    goToToday() {
      const now = new Date();
      this.currentYear = now.getFullYear();
      this.currentMonth = now.getMonth();
      this.selectedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      this.currentWeekStart = this.getWeekStart(now);
      this.emitDateRange();
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
      this.clampSelectedDate();
      this.emitDateRange();
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
      this.clampSelectedDate();
      this.emitDateRange();
    },
    prevWeek() {
      const start = new Date(this.weekStartDate);
      start.setDate(start.getDate() - 7);
      this.currentWeekStart = start;
      this.emitDateRange();
    },
    nextWeek() {
      const start = new Date(this.weekStartDate);
      start.setDate(start.getDate() + 7);
      this.currentWeekStart = start;
      this.emitDateRange();
    },
    prevYear() {
      this.currentYear--;
      this.emitDateRange();
    },
    nextYear() {
      this.currentYear++;
      this.emitDateRange();
    },
    clampSelectedDate() {
      const prevDay = this.selectedDate ? this.selectedDate.getDate() : new Date().getDate();
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
      this.selectedDate = new Date(this.currentYear, this.currentMonth, Math.min(prevDay, lastDay));
    },
    openMonth(monthIndex) {
      this.currentMonth = monthIndex;
      this.selectedDate = new Date(this.currentYear, monthIndex, 1);
      this.$emit("change-view", { mode: "month", date: this.selectedDate });
    },
    formatRange(startDate, endDate) {
      const start = moment(startDate).startOf("day");
      const end = moment(endDate).startOf("day");
      if (start.isSame(end, "day")) {
        return start.locale(this.locale).format("D MMM YYYY");
      }
      return `${start.locale(this.locale).format("D MMM")} – ${end.locale(this.locale).format("D MMM YYYY")}`;
    },
    // Katalog: sadece oluşturulma tarihi; atama: gerçek start–end
    formatItemDate(item) {
      if (item.type === "training") {
        return moment(item.startDate).locale(this.locale).format("D MMM YYYY");
      }
      if (item.periodStartDate || item.deadlineDate) {
        return this.formatRange(
          item.periodStartDate || item.startDate,
          item.deadlineDate || item.endDate
        );
      }
      return this.formatRange(item.startDate, item.endDate);
    },
    formatShortDate(date) {
      return moment(date).locale(this.locale).format("D MMM");
    },
    typeLabel(item) {
      if (item.type === "notice") {
        return this.$t("general.announcement");
      }
      if (item.type === "training") {
        return this.$t("education.catalog_education");
      }
      return this.$t("general.assigned_educations") || "Atanan Eğitim";
    },
    onItemClick(item) {
      this.$emit("item-click", item);
    },
    formatDateKey(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    getMonthGridRange() {
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
      const startDow = (firstDay.getDay() + 6) % 7;
      const gridStart = new Date(firstDay);
      gridStart.setDate(gridStart.getDate() - startDow);
      const totalCells = startDow + lastDay.getDate();
      const remaining = 7 - (totalCells % 7);
      const gridEnd = new Date(lastDay);
      if (remaining < 7) {
        gridEnd.setDate(gridEnd.getDate() + remaining);
      }
      return { start: this.formatDateKey(gridStart), end: this.formatDateKey(gridEnd) };
    },
    getWeekRange() {
      const start = this.weekStartDate;
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { start: this.formatDateKey(start), end: this.formatDateKey(end) };
    },
    emitDateRange() {
      const mode = this.viewMode;
      if (mode === "year") {
        this.$emit("date-range-change", { year: this.currentYear, mode: "year" });
      } else if (mode === "week") {
        const range = this.getWeekRange();
        this.$emit("date-range-change", { start: range.start, end: range.end, mode: "week" });
      } else {
        const range = this.getMonthGridRange();
        this.$emit("date-range-change", { start: range.start, end: range.end, mode: "month" });
      }
    },
    isDeadlineSoon(item) {
      if (item.type !== "assignment" || item.isExpired) return false;

      const deadline = item.deadlineDate || item.endDate;
      if (!deadline) return false;

      const now = moment();
      const end = moment(deadline);
      if (end.isSameOrBefore(now)) return false;

      const daysUntilEnd = end.clone().startOf("day").diff(now.clone().startOf("day"), "days");
      return daysUntilEnd >= 0 && daysUntilEnd <= 7;
    },
    isUpcoming(item) {
      if (item.isExpired) return false;
      // deadline_soon öncelikli — ikisi aynı anda olamaz
      if (this.isDeadlineSoon(item)) return false;
      const today = moment().startOf("day");
      const start = moment(item.startDate).startOf("day");
      return start.isAfter(today, "day") && start.diff(today, "days") <= 14;
    },
  },
};
</script>

<style lang="scss" scoped src="./agenda-calendar.scss"></style>
